import type { LucideIcon } from "lucide-react";
import {
  Cake,
  Calendar,
  Clock,
  Hash,
  Layers,
  Mail,
  Phone,
  Tag,
  User,
} from "lucide-react";
import type { ContactTableColumnId } from "./contacts-column-view.js";

export const contactsTableShellClass =
  "rounded-xl border border-line/70 bg-surface shadow-[inset_0_1px_0_rgba(255,255,255,0.55)]";

export const contactsTableClass =
  "w-full min-w-[56rem] border-separate border-spacing-0 text-[0.8125rem]";

export const contactsTableHeaderFillClass =
  "bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)]";

/** Combined x/y scrollport so thead cells can stick vertically and pinned cells horizontally. */
export const contactsTableScrollViewportClass =
  "min-h-0 max-w-full flex-1 overflow-auto overscroll-contain";

export const contactsTableHeadRowClass = `border-b border-line/80 ${contactsTableHeaderFillClass} text-start`;

export const contactsTableHeaderCellClass =
  "border-e border-line/50 px-3 py-2.5 align-middle font-semibold text-ink-subtle last:border-e-0";

export const contactsTableBodyRowClass =
  "group/row border-b border-line/45 transition-colors hover:bg-surface-muted/50 data-[selected=true]:bg-accent/4";

export const contactsTableBodyCellClass =
  "border-e border-line/35 px-3 py-2.5 align-middle last:border-e-0";

export const contactsTableHeaderLabelClass =
  "inline-flex min-w-0 items-center gap-1.5 text-[0.72rem] font-semibold tracking-[0.03em] uppercase";

export const contactsTableHeaderIconWrapClass =
  "inline-grid size-6 shrink-0 place-items-center rounded-md border border-line/60 bg-surface text-ink-muted shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";

/**
 * Vertically sticky unpinned headers. Below pinned/chrome headers, above body cells.
 * Opaque fill matches the rest of the header row (no white pinned-header mismatch).
 */
export const contactsTableHeaderStickyTopClass = `sticky top-0 z-[11] border-b border-line/80 ${contactsTableHeaderFillClass}`;

/**
 * Vertically + horizontally sticky chrome / pinned headers.
 * Same fill as unpinned headers; higher z so intersection cells stay above scrolling columns.
 */
export const contactsTableStickyHeaderCellClass = `sticky top-0 z-[12] border-b border-line/80 ${contactsTableHeaderFillClass}`;

export const contactsTableStickyBodyCellClass =
  "sticky z-[1] bg-surface group-data-[selected=true]/row:bg-[color-mix(in_srgb,var(--color-accent)_4%,white)] group-hover/row:bg-surface-muted";

export const contactsViewportPopoverClass =
  "max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-y-auto overscroll-contain";

export const contactsViewportDropdownClass =
  "max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto overscroll-contain";

export const contactsTablePinnedEdgeClass = "border-e border-line/80";

export const contactsTableHoverViewClass =
  "absolute end-full top-1/2 z-[2] me-1 hidden h-6 -translate-y-1/2 items-center rounded-md border border-line/70 bg-surface px-1.5 text-[0.6875rem] font-semibold text-foreground shadow-sm [@media(hover:hover)]:!inline-flex [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/row:opacity-100 [@media(hover:hover)]:focus-visible:opacity-100";

export const contactsValueChipClass =
  "inline-flex max-w-full min-w-0 items-center truncate rounded-full border border-line/60 bg-surface px-2.5 py-0.5 text-[0.6875rem] font-medium text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]";

export const contactsGroupChipClass =
  "inline-flex max-w-[8rem] truncate rounded-full border border-accent/20 bg-accent/10 px-2 py-0.5 text-[0.6875rem] font-medium text-foreground";

export const contactsGroupMoreChipClass =
  "inline-flex h-6 max-w-full items-center rounded-full border border-line/55 bg-surface-muted/80 px-2 text-[0.6875rem] font-medium text-muted hover:border-accent/25 hover:text-foreground";

export const contactsBulkBarClass =
  "flex flex-wrap items-center justify-between gap-3 border-b border-accent/15 bg-accent/6 px-4 py-2.5";

export const CONTACT_SHEET_MOTION_CLASS =
  "data-[state=closed]:animate-sheet-out-end rtl:data-[state=closed]:animate-sheet-out-start";

export const CONTACT_SHEET_OVERLAY_MOTION_CLASS = "data-[state=closed]:animate-overlay-out";

export const CONTACT_TABLE_COLUMN_META: Record<
  ContactTableColumnId,
  { icon?: LucideIcon; align?: "start" | "end" }
> = {
  firstName: { icon: User },
  lastName: { icon: User },
  displayName: { icon: User },
  email: { icon: Mail },
  phone: { icon: Phone },
  birthday: { icon: Cake },
  groups: { icon: Tag },
  platforms: { icon: Layers },
  createdAt: { icon: Calendar },
  updatedAt: { icon: Clock },
};

export function contactsTableColumnFallbackIcon(id: ContactTableColumnId): LucideIcon {
  return CONTACT_TABLE_COLUMN_META[id]?.icon ?? Hash;
}
